import { getCurrentLanguage } from '../../../packages/src/localization/index.js';
import {
  fetchLocalizationFilesManifest,
  getSystemDefaultLocalizationFile,
  hasResolvedLocalizationFile,
  hasResolvedLocalizationFileContent,
  resolveLocalizationFile,
} from '../../../packages/src/localization/localizationFiles.js';
import { getLocalizationContext } from '../js/localization.js';

const contentCache = new Map();

/**
 * Resolves webapp HTML content from the standalone localization-files system.
 *
 * Example input:
 * - `contentId = 'faq'`
 * - `options = { orgId: 12, eventCode: 'wmww', language: 'fr' }`
 *
 * Example output:
 * - resolved FAQ HTML for that language and event
 *
 * Edge cases:
 * - falls back to repo defaults when org/event context is missing
 * - caches resolved values for the same scope during the current page session
 */
export async function getLocalizationHtmlContent({ contentId, ...options }) {
  const context = resolveContext(options);
  const cacheKey = `${context.orgId || 'default'}:${context.eventCode || 'global'}:${context.language}:${contentId}`;
  const shouldUseCache = typeof options.fetcher !== 'function';

  if (shouldUseCache && contentCache.has(cacheKey)) {
    return contentCache.get(cacheKey);
  }

  const promise = context.orgId
    ? ensureLocalizationManifestLoaded({ ...options, ...context }).then(() =>
        resolveLocalizationFile(
          {
            orgId: context.orgId,
            eventCode: context.eventCode,
            language: context.language,
            contentId,
          },
          { fetcher: options.fetcher }
        )
      )
    : Promise.resolve(getSystemDefaultLocalizationFile({ contentId, language: context.language }));

  if (shouldUseCache) {
    contentCache.set(cacheKey, promise);
  }

  return promise;
}

export async function hasLocalizationHtmlContent({ contentId, ...options }) {
  const context = resolveContext(options);

  if (!context.orgId) {
    return hasResolvedLocalizationFileContent(
      getSystemDefaultLocalizationFile({
        contentId,
        language: context.language,
      })
    );
  }

  await ensureLocalizationManifestLoaded({ ...options, ...context });

  return hasResolvedLocalizationFile(
    {
      orgId: context.orgId,
      eventCode: context.eventCode,
      language: context.language,
      contentId,
    },
    { fetcher: options.fetcher }
  );
}

async function ensureLocalizationManifestLoaded(options = {}) {
  const context = resolveContext(options);

  if (!context.orgId) {
    return {};
  }

  return fetchLocalizationFilesManifest({
    orgId: context.orgId,
  }, { fetcher: options.fetcher });
}

export async function getFaqContentHtml(options) {
  return getLocalizationHtmlContent({ contentId: 'faq', ...options });
}

export async function hasFaqContentHtml(options) {
  return hasLocalizationHtmlContent({ contentId: 'faq', ...options });
}

export async function getPrivacyContentHtml(options) {
  return getLocalizationHtmlContent({ contentId: 'privacy', ...options });
}

export async function hasPrivacyContentHtml(options) {
  return hasLocalizationHtmlContent({ contentId: 'privacy', ...options });
}

export function hasVisibleHtmlContent(htmlContent = '') {
  return hasResolvedLocalizationFileContent(htmlContent);
}

function resolveContext(options = {}) {
  const storedContext = getLocalizationContext();

  return {
    orgId: options.orgId ?? storedContext.orgId,
    eventCode: options.eventCode ?? storedContext.eventCode ?? 'global',
    language: normalizeLanguage(options.language ?? getCurrentLanguage()),
  };
}

function normalizeLanguage(language = 'en') {
  return `${language || 'en'}`.split('-')[0];
}
